package com.iptvplayer.web.ui.screen

import androidx.compose.runtime.*
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.presentation.state.MediaTrack
import com.iptvplayer.shared.presentation.viewmodel.PlayerViewModel
import com.iptvplayer.web.ui.*
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event

/** How long a VOD guest waits for the host's P2P offer before playing directly. */
private const val P2P_WAIT_MS = 10_000L

@Composable
fun PlayerScreen(route: Route.Player, onBack: () -> Unit) {
    val vm = remember(route.url) { koinGet<PlayerViewModel>() }
    val state by vm.state.collectAsState()

    // Watch Together state. roomId set => we're in a party; isHost => we created it.
    var videoEl by remember(route.url) { mutableStateOf<HTMLVideoElement?>(null) }
    // Branded loading: poster + title overlay until the first frame renders.
    var firstFrame by remember(route.url) { mutableStateOf(false) }
    var roomId by remember(route.url) { mutableStateOf(route.watchRoomId) }
    var isHost by remember(route.url) { mutableStateOf(false) }
    var viewers by remember(route.url) { mutableStateOf(0) }
    var watchError by remember(route.url) { mutableStateOf<String?>(null) }
    var copied by remember(route.url) { mutableStateOf(false) }
    var watchSession by remember(route.url) { mutableStateOf<dynamic>(null) }
    // Watch Together socket state: "connected" | "reconnecting" | "disconnected".
    var connState by remember(route.url) { mutableStateOf("connected") }
    // Live-channel bounded auto-retry: count since last successful playback,
    // and a nonce that forces the <video> to be recreated/reattached.
    var liveRetryCount by remember(route.url) { mutableStateOf(0) }
    var attachNonce by remember(route.url) { mutableStateOf(0) }
    // Audio/subtitle selection: the currently attached engine (hls.js/mpegts
    // instance, or null for native <video>) and whether it's hls.js.
    var attachedPlayer by remember(route.url) { mutableStateOf<dynamic>(null) }
    var attachedIsHls by remember(route.url) { mutableStateOf(false) }
    var showTracks by remember(route.url) { mutableStateOf(false) }
    val chatLines = remember(route.url) { mutableStateListOf<ChatLine>() }
    var chatInput by remember(route.url) { mutableStateOf("") }
    var showChat by remember(route.url) { mutableStateOf(false) }
    var inVoice by remember(route.url) { mutableStateOf(false) }
    var muted by remember(route.url) { mutableStateOf(false) }

    // WebCodecs fallback for MKV/HEVC the native <video> can't play.
    var useWebCodecs by remember(route.url) { mutableStateOf(false) }
    var wcError by remember(route.url) { mutableStateOf<String?>(null) }
    var wcPos by remember(route.url) { mutableStateOf(0.0) }
    var wcDur by remember(route.url) { mutableStateOf(0.0) }
    var wcPlaying by remember(route.url) { mutableStateOf(true) }
    var wcHandle by remember(route.url) { mutableStateOf<dynamic>(null) }
    val eligibleWc = remember(route.url) { canFallBackToWebCodecs(route.url, route.type) }

    // Live rooms: every guest plays the server tee relay — host platform
    // doesn't matter. VOD rooms: guests watch the host's WebRTC broadcast…
    // unless the host is a native app (no captureStream there), in which case
    // we stop waiting after a timeout and play the stream directly, following
    // the room's server-side clock.
    val isLiveContent = route.type == ContentType.CHANNEL ||
        route.url.lowercase().let { it.contains("/live/") || it.contains(".ts") }
    var p2pTimedOut by remember(route.url) { mutableStateOf(false) }
    val receiveMode = roomId != null && !isHost && !isLiveContent && !p2pTimedOut
    var hostStream by remember(route.url) { mutableStateOf<dynamic>(null) }

    // No P2P offer within the window ⇒ the host can't broadcast (native app).
    // Flipping p2pTimedOut re-keys the connect effect below, which tears the
    // session down and reconnects as a direct-playing sync follower.
    LaunchedEffect(roomId, isHost, isLiveContent) {
        if (roomId != null && !isHost && !isLiveContent) {
            delay(P2P_WAIT_MS)
            if (hostStream == null) p2pTimedOut = true
        }
    }

    LaunchedEffect(route.url) {
        vm.initPlayer(
            route.contentId, route.name, route.url, route.type, route.logo,
            seriesSourceId = route.seriesSourceId, seriesId = route.seriesId,
        )
    }
    DisposableEffect(route.url) {
        onDispose { vm.stop() }
    }

    // VOD finished with nothing to auto-play next — close the player gracefully.
    LaunchedEffect(state.playbackCompleted) {
        if (state.playbackCompleted) onBack()
    }
    // "Up next: …" toast for the auto-started episode; hide after a few seconds.
    LaunchedEffect(state.upNext) {
        if (state.upNext != null) {
            delay(4_000)
            vm.clearUpNext()
        }
    }

    // Apply the chosen audio track to whichever engine is attached.
    // (Keyed on attachedPlayer so a re-attach re-applies the selection.)
    LaunchedEffect(state.selectedAudioId, attachedPlayer, videoEl) {
        val idx = state.selectedAudioId?.toIntOrNull() ?: return@LaunchedEffect
        val p = attachedPlayer
        val v = videoEl
        runCatching {
            if (attachedIsHls && p != null) {
                p.audioTrack = idx
            } else if (v != null) {
                val at = v.asDynamic().audioTracks
                if (jsTypeOf(at) != "undefined" && at != null) {
                    val n = (at.length as? Int) ?: 0
                    for (i in 0 until n) at[i].enabled = (i == idx)
                }
            }
        }
    }
    // Apply the chosen subtitle track (null = off).
    LaunchedEffect(state.selectedTextId, attachedPlayer, videoEl) {
        val idx = state.selectedTextId?.toIntOrNull() ?: -1
        val p = attachedPlayer
        val v = videoEl
        runCatching {
            if (attachedIsHls && p != null) {
                p.subtitleTrack = idx // -1 = off
            } else if (v != null) {
                val tt = v.asDynamic().textTracks
                if (jsTypeOf(tt) != "undefined" && tt != null) {
                    val n = (tt.length as? Int) ?: 0
                    for (i in 0 until n) tt[i].mode = if (i == idx) "showing" else "disabled"
                }
            }
        }
    }

    // Connect to the sync controller once both the room and the <video> exist.
    // Keyed on p2pTimedOut so the P2P-guest → sync-follower fallback actually
    // reconnects (and re-binds to whichever <video> element is current).
    DisposableEffect(roomId, videoEl, p2pTimedOut) {
        val rid = roomId
        val v = videoEl
        if (rid != null && v != null) {
            val opts = js("({})")
            opts.announceOnOpen = isHost
            opts.receiveOnly = receiveMode || (!isHost && isLiveContent)
            opts.onPresence = { n: Int -> viewers = n }
            opts.onChat = { name: String, text: String, mine: Boolean -> chatLines.add(ChatLine(name, text, mine)) }
            opts.onVoice = { st: dynamic ->
                inVoice = (st.inVoice as? Boolean) ?: false
                muted = (st.muted as? Boolean) ?: false
            }
            opts.onBroadcastStream = { stream: dynamic -> hostStream = stream }
            opts.onConnState = { s: String -> connState = s }
            watchSession = window.asDynamic().IptvWatch.connect(v, WatchApi.wsUrl(rid), opts)
        }
        onDispose {
            val s = watchSession
            if (s != null) window.asDynamic().IptvWatch.leave(s)
            watchSession = null
        }
    }

    // Host: once playing, capture the <video> and broadcast it to guests (P2P).
    LaunchedEffect(watchSession, videoEl, isHost) {
        val v = videoEl
        val s = watchSession
        if (isHost && s != null && v != null) {
            val capture = {
                val d = v.asDynamic()
                val stream = runCatching {
                    if (d.captureStream != null) d.captureStream()
                    else if (d.mozCaptureStream != null) d.mozCaptureStream() else null
                }.getOrNull()
                if (stream != null) window.asDynamic().IptvWatch.setBroadcastStream(s, stream)
            }
            // { once: true } so the listener removes itself — the plain
            // addEventListener leaked (the video's onDispose never removed it).
            if (v.readyState >= 2) capture()
            else v.asDynamic().addEventListener("loadeddata", { _: dynamic -> capture() }, js("({ once: true })"))
        }
    }

    // Guest: play the host's received stream.
    LaunchedEffect(hostStream, videoEl) {
        val v = videoEl
        val s = hostStream
        if (receiveMode && v != null && s != null) {
            v.asDynamic().srcObject = s
            runCatching { v.asDynamic().play() }
        }
    }

    Div(attrs = {
        style {
            position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
            property("background", "#000"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); property("z-index", "20")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "14px")
                property("padding", "12px 16px"); property("background", "rgba(0,0,0,0.55)")
            }
        }) {
            GhostButton("← Back") { onBack() }
            Span(attrs = { style { property("font-weight", "600"); ellipsis() } }) { Text(route.name) }
            Div(attrs = { style { property("flex-grow", "1") } }) {}
            if (roomId != null) {
                Span(attrs = { style { property("color", Theme.textDim); property("font-size", "13px") } }) {
                    Text("👥 $viewers")
                }
                GhostButton(if (showChat) "💬 Hide" else "💬 Chat") { showChat = !showChat }
                if (!inVoice) {
                    GhostButton("🎤 Voice") { window.asDynamic().IptvWatch.joinVoice(watchSession) }
                } else {
                    GhostButton(if (muted) "🔇 Unmute" else "🎙 Mute") {
                        window.asDynamic().IptvWatch.setMuted(watchSession, !muted)
                    }
                }
            } else {
                // Only a logged-in host can start a party (needs an access token).
                GhostButton("👥 Watch Together") {
                    watchError = null
                    WatchApi.createRoom(
                        streamUrl = route.url,
                        contentId = route.contentId,
                        name = route.name,
                        type = route.type.name,
                        logo = route.logo,
                        onOk = { id -> roomId = id; isHost = true },
                        onErr = { e -> watchError = "Couldn't start watch party ($e)" },
                    )
                }
            }
            // Audio/subtitle picker — only when the stream actually reported tracks.
            if (state.audioTracks.isNotEmpty() || state.textTracks.isNotEmpty()) {
                GhostButton("⚙ Tracks") { showTracks = !showTracks }
            }
            GhostButton(if (state.isFavorite) "★ Favorited" else "☆ Favorite") { vm.toggleFavorite() }
        }

        // ── Audio / subtitle selection popover ──
        if (showTracks && (state.audioTracks.isNotEmpty() || state.textTracks.isNotEmpty())) {
            Div(attrs = {
                style {
                    position(Position.Absolute); top(58.px); right(12.px)
                    property("background", "rgba(0,0,0,0.92)")
                    property("border", "1px solid ${Theme.border}"); property("border-radius", "10px")
                    property("padding", "8px"); property("z-index", "35")
                    property("min-width", "210px"); property("max-width", "300px")
                    property("max-height", "60vh"); property("overflow-y", "auto")
                }
            }) {
                if (state.audioTracks.isNotEmpty()) {
                    TrackSectionLabel("Audio")
                    state.audioTracks.forEach { t ->
                        TrackOption(t.label, selected = state.selectedAudioId == t.id) { vm.selectAudioTrack(t.id) }
                    }
                }
                if (state.textTracks.isNotEmpty()) {
                    TrackSectionLabel("Subtitles")
                    TrackOption("Off", selected = state.selectedTextId == null) { vm.selectTextTrack(null) }
                    state.textTracks.forEach { t ->
                        TrackOption(t.label, selected = state.selectedTextId == t.id) { vm.selectTextTrack(t.id) }
                    }
                }
            }
        }

        // Share bar — shown to the host so they can hand out the join link.
        if (roomId != null && isHost) {
            val link = WatchApi.shareLink(roomId!!)
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "10px")
                    property("padding", "8px 16px"); property("background", "rgba(124,58,237,0.18)")
                    property("border-bottom", "1px solid ${Theme.border}")
                }
            }) {
                Span(attrs = { style { property("font-size", "13px"); property("color", Theme.text) } }) { Text("Share this link:") }
                Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                    attr("value", link); attr("readonly", "")
                    style { property("flex-grow", "1"); property("background", Theme.surface); property("color", Theme.text); property("border", "1px solid ${Theme.border}"); property("border-radius", "6px"); property("padding", "6px 10px"); property("font-size", "12px") }
                })
                GhostButton(if (copied) "✓ Copied" else "Copy") {
                    // Only show success once the clipboard write actually resolves,
                    // and reset the label after a couple of seconds.
                    runCatching {
                        window.navigator.asDynamic().clipboard.writeText(link).then({ _: dynamic ->
                            copied = true
                            window.setTimeout({ copied = false }, 2_000)
                        }, { _: dynamic -> copied = false })
                    }
                }
            }
        }

        // Watch Together socket health (fed by watchtogether.js onConnState).
        if (roomId != null && connState != "connected") {
            Div(attrs = {
                style {
                    property("padding", "6px 16px"); property("font-size", "13px")
                    property("background", "rgba(234,179,8,0.15)"); property("color", "#facc15")
                    property("border-bottom", "1px solid ${Theme.border}")
                }
            }) {
                Text(
                    if (connState == "reconnecting") "Reconnecting to the watch party…"
                    else "Lost connection to the watch party."
                )
            }
        }

        Div(attrs = {
            style {
                property("flex-grow", "1"); property("min-height", "0"); property("position", "relative")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            }
        }) {
            // In a room, LIVE channels use the server fan-out relay (one provider
            // connection for everyone). VOD plays via the normal proxy per viewer
            // (full-file relay caching is too heavy for the droplet).
            val rid = roomId
            val relaySrc = if (rid != null && isLiveContent) WatchApi.relayUrl(rid) else null
            // The VM owns the playing URL (it changes when a series episode
            // auto-advances); route.url is only the initial one.
            val activeUrl = state.streamUrl.ifBlank { route.url }
            if (receiveMode) {
                // Guest: a <video> fed by the host's WebRTC stream (no provider connection).
                if (hostStream == null) {
                    Spinner("Connecting to the host's stream…")
                }
                Video(attrs = {
                    attr("controls", ""); attr("autoplay", ""); attr("playsinline", "")
                    style {
                        property("width", "100%"); property("height", "100%"); property("max-height", "100%"); property("object-fit", "contain")
                        if (hostStream == null) property("display", "none")
                    }
                    ref { video -> videoEl = video; onDispose { videoEl = null } }
                }) {}
            } else if (useWebCodecs) {
                // MKV/HEVC: decode via WebCodecs (mediabunny) and render to a canvas.
                Canvas(attrs = {
                    style { property("width", "100%"); property("height", "100%"); property("max-height", "100%"); property("object-fit", "contain"); property("background", "#000") }
                    ref { canvas ->
                        val src = relaySrc ?: proxied(route.url)
                        val handle = attachWebCodecs(
                            canvas, src,
                            onReady = { d -> wcDur = d },
                            onTime = { cur, dur -> wcPos = cur; if (dur > 0) wcDur = dur },
                            onState = { p -> wcPlaying = p },
                            onEnded = { wcPlaying = false },
                            onError = { msg -> wcError = msg },
                        )
                        wcHandle = handle
                        onDispose { detachWebCodecs(handle); wcHandle = null }
                    }
                }) {}
            } else {
                // Re-keyed on the active URL (series auto-advance), the VM's
                // retryToken (Retry button) and attachNonce (live auto-retry)
                // so any of them recreates the <video> and re-attaches the stream.
                key(relaySrc, activeUrl, state.retryToken, attachNonce) {
                Video(attrs = {
                    attr("controls", "")
                    attr("autoplay", "")
                    attr("playsinline", "")
                    attr("preload", "auto") // start downloading immediately
                    route.logo?.let { attr("poster", proxied(it)) }
                    style { property("width", "100%"); property("height", "100%"); property("max-height", "100%"); property("object-fit", "contain") }
                    ref { video ->
                        val v = video
                        videoEl = v // expose to the Watch Together sync controller
                        // Shared failure path for native <video> errors and engine
                        // (hls.js/mpegts.js) fatal errors — first one wins.
                        var failed = false
                        val onFailure: (String) -> Unit = fail@{ msg ->
                            if (failed) return@fail
                            failed = true
                            // Fall back to WebCodecs for unplayable containers/codecs (MKV/HEVC).
                            // Works in rooms too (the relay is range-capable), though the canvas
                            // path isn't WS-synced — best-effort for MKV parties.
                            if (eligibleWc &&
                                (js("typeof window.IptvWC !== 'undefined' && window.IptvWC.supported()") as Boolean)
                            ) {
                                useWebCodecs = true
                            } else if (isLiveContent && liveRetryCount < 3) {
                                // Live channels: bounded auto-retry (1s/2s/4s backoff)
                                // before surfacing the error.
                                val attempt = liveRetryCount + 1
                                vm.updateBufferingState(true)
                                window.setTimeout({
                                    liveRetryCount = attempt
                                    attachNonce += 1 // recreate the <video> → re-attach
                                }, 1_000 * (1 shl (attempt - 1)))
                            } else {
                                vm.updateBufferingState(false)
                                vm.onError(msg)
                            }
                        }
                        val player = attachStream(
                            v, activeUrl, route.type == ContentType.CHANNEL, relaySrc, onFailure,
                            onTracks = { audio, text -> vm.onTracksChanged(audio, text) },
                        )
                        attachedPlayer = player
                        // attachStream returns non-null for .m3u8 only when hls.js took it.
                        attachedIsHls = player != null && activeUrl.lowercase().contains(".m3u8")
                        val onPlaying: (Event) -> Unit = {
                            firstFrame = true
                            liveRetryCount = 0 // healthy again — reset the retry budget
                            vm.updateBufferingState(false)
                        }
                        v.addEventListener("playing", onPlaying)
                        val onWaiting: (Event) -> Unit = { vm.updateBufferingState(true) }
                        v.addEventListener("waiting", onWaiting)
                        val onTime: (Event) -> Unit = {
                            val dur = if (v.duration.isFinite()) (v.duration * 1000).toLong() else 0L
                            vm.updatePlaybackState(!v.paused, (v.currentTime * 1000).toLong(), dur)
                        }
                        v.addEventListener("timeupdate", onTime)
                        // Resume: on first metadata of a directly-played VOD stream
                        // (not live, not a sync-following room guest), seek to the
                        // saved position the VM loaded in initPlayer.
                        val onMeta: (Event) -> Unit = {
                            if (!isLiveContent && (roomId == null || isHost)) {
                                val pos = vm.state.value.currentPositionMs
                                val durMs = if (v.duration.isFinite()) (v.duration * 1000).toLong() else Long.MAX_VALUE
                                if (pos > 0 && pos < durMs - 5_000) {
                                    runCatching { v.currentTime = pos / 1000.0 }
                                }
                            }
                            // Native playback: enumerate <video> tracks where the
                            // browser exposes them (hls.js reports its own via events).
                            if (player == null) {
                                reportNativeTracks(v) { audio, text -> vm.onTracksChanged(audio, text) }
                            }
                        }
                        v.asDynamic().addEventListener("loadedmetadata", onMeta, js("({ once: true })"))
                        // Ended VOD → auto-advance the series episode / graceful close.
                        // Room guests don't own playback, so only solo viewers and hosts.
                        val onEnded: (Event) -> Unit = {
                            vm.updateBufferingState(false)
                            if (route.type != ContentType.CHANNEL && (roomId == null || isHost)) {
                                vm.onPlaybackEnded()
                            }
                        }
                        v.addEventListener("ended", onEnded)
                        val onErr: (Event) -> Unit = {
                            onFailure("Playback error — this stream may be unsupported in the browser.")
                        }
                        v.addEventListener("error", onErr)
                        onDispose {
                            v.removeEventListener("playing", onPlaying)
                            v.removeEventListener("waiting", onWaiting)
                            v.removeEventListener("timeupdate", onTime)
                            v.removeEventListener("ended", onEnded)
                            v.removeEventListener("error", onErr)
                            v.removeEventListener("loadedmetadata", onMeta)
                            detachStream(player)
                            runCatching { v.pause() }
                            videoEl = null
                            attachedPlayer = null
                            attachedIsHls = false
                            // Drop stale track lists; a re-attach re-reports them.
                            vm.onTracksChanged(emptyList(), emptyList())
                        }
                    }
                }) {}
                }
                // Poster-branded loading overlay: blurred art + title + spinner,
                // fades away once the first frame is on screen.
                if (!firstFrame && state.error == null) {
                    Div(attrs = {
                        style {
                            property("position", "absolute"); property("inset", "0")
                            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                            property("gap", "14px"); property("background", "#000")
                            property("pointer-events", "none"); property("z-index", "2")
                            property("transition", "opacity .4s")
                        }
                    }) {
                        route.logo?.let { logo ->
                            Img(src = proxied(logo), attrs = {
                                style {
                                    property("position", "absolute"); property("inset", "0")
                                    property("width", "100%"); property("height", "100%")
                                    property("object-fit", "cover"); property("filter", "blur(28px) brightness(0.35)")
                                }
                            })
                            Img(src = proxied(logo), attrs = {
                                style {
                                    property("max-height", "180px"); property("max-width", "300px")
                                    property("border-radius", "12px"); property("z-index", "1")
                                }
                            })
                        }
                        Span(attrs = {
                            style {
                                property("z-index", "1"); property("font-size", "20px")
                                property("font-weight", "700"); property("color", "#fff")
                                property("text-align", "center"); property("max-width", "70%")
                            }
                        }) { Text(route.name) }
                        Div(attrs = {
                            style {
                                property("z-index", "1")
                                property("width", "34px"); property("height", "34px")
                                property("border", "3px solid rgba(255,255,255,0.2)")
                                property("border-top-color", Theme.primary)
                                property("border-radius", "50%")
                                property("animation", "iptvspin 1s linear infinite")
                            }
                        }) {}
                        Span(attrs = {
                            style { property("z-index", "1"); property("color", "rgba(255,255,255,0.65)"); property("font-size", "14px") }
                        }) {
                            Text(if (route.type == ContentType.CHANNEL) "Tuning in…" else "Preparing your stream…")
                        }
                    }
                }
            }
        }

        // Controls for the WebCodecs (canvas) path — the canvas has no native chrome.
        if (useWebCodecs && wcError == null) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "12px")
                    property("padding", "10px 16px"); property("background", "rgba(0,0,0,0.55)")
                }
            }) {
                GhostButton(if (wcPlaying) "⏸ Pause" else "▶ Play") {
                    val h = wcHandle
                    if (h != null) { if (wcPlaying) window.asDynamic().IptvWC.pause(h) else window.asDynamic().IptvWC.play(h) }
                }
                Span(attrs = { style { property("font-size", "13px"); property("color", Theme.textDim); property("min-width", "44px") } }) { Text(fmtTime(wcPos)) }
                Input(type = org.jetbrains.compose.web.attributes.InputType.Range, attrs = {
                    attr("min", "0"); attr("max", if (wcDur > 0) wcDur.toInt().toString() else "0")
                    attr("value", wcPos.toInt().toString()); attr("step", "1")
                    style { property("flex-grow", "1") }
                    onInput { ev ->
                        val t = (ev.asDynamic().target.value as String).toDoubleOrNull() ?: 0.0
                        wcPos = t
                        val h = wcHandle
                        if (h != null) window.asDynamic().IptvWC.seek(h, t)
                    }
                })
                Span(attrs = { style { property("font-size", "13px"); property("color", Theme.textDim); property("min-width", "44px") } }) { Text(fmtTime(wcDur)) }
            }
        }

        // ── Chat panel (Watch Together) ──
        if (roomId != null && showChat) {
            Div(attrs = {
                style {
                    position(Position.Absolute); top(58.px); right(0.px); bottom(0.px); width(320.px)
                    property("max-width", "85vw"); property("background", "rgba(0,0,0,0.88)")
                    property("border-left", "1px solid ${Theme.border}")
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); property("z-index", "30")
                }
            }) {
                Div(attrs = {
                    style {
                        property("flex-grow", "1"); property("overflow-y", "auto"); property("padding", "12px")
                        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); property("gap", "6px")
                    }
                }) {
                    if (chatLines.isEmpty()) {
                        Span(attrs = { style { property("color", Theme.textDim); property("font-size", "13px") } }) { Text("No messages yet. Say hi 👋") }
                    }
                    chatLines.forEach { line ->
                        Div(attrs = { style { property("font-size", "13px"); property("line-height", "1.4") } }) {
                            Span(attrs = { style { property("font-weight", "700"); property("color", if (line.mine) "#4f8cff" else "#22d3ee") } }) {
                                Text(if (line.mine) "You" else line.name)
                            }
                            Span { Text(": ${line.text}") }
                        }
                    }
                }
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); property("gap", "6px"); property("padding", "10px")
                        property("border-top", "1px solid ${Theme.border}")
                    }
                }) {
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                        attr("value", chatInput); attr("placeholder", "Message…")
                        style {
                            property("flex-grow", "1"); property("background", Theme.surface); property("color", Theme.text)
                            property("border", "1px solid ${Theme.border}"); property("border-radius", "6px"); property("padding", "8px")
                        }
                        onInput { ev -> chatInput = ev.value }
                        onKeyUp { ev ->
                            if (ev.key == "Enter" && chatInput.isNotBlank()) {
                                window.asDynamic().IptvWatch.sendChat(watchSession, chatInput); chatInput = ""
                            }
                        }
                    })
                    GhostButton("Send") {
                        if (chatInput.isNotBlank()) { window.asDynamic().IptvWatch.sendChat(watchSession, chatInput); chatInput = "" }
                    }
                }
            }
        }

        // "Up next: …" toast when a series episode auto-advances.
        state.upNext?.let { label ->
            Div(attrs = {
                style {
                    position(Position.Absolute); property("bottom", "84px"); property("left", "50%")
                    property("transform", "translateX(-50%)")
                    property("background", "rgba(0,0,0,0.85)"); property("border", "1px solid ${Theme.border}")
                    property("border-radius", "10px"); property("padding", "10px 18px")
                    property("z-index", "40"); property("font-size", "14px"); property("color", "#fff")
                    property("white-space", "nowrap"); property("max-width", "85vw"); property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
            }) { Text("Up next: $label") }
        }

        (wcError ?: watchError ?: state.error)?.let { err ->
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "12px")
                    property("padding", "10px 16px")
                }
            }) {
                Span(attrs = { style { property("color", Theme.danger); property("font-size", "14px") } }) { Text(err) }
                // Retry only for player errors (not watch-party/WebCodecs ones):
                // vm.retry() clears the error and bumps retryToken, which re-keys
                // the <video> above and re-attaches the stream.
                if (wcError == null && watchError == null && state.error != null) {
                    GhostButton("↻ Retry") {
                        firstFrame = false
                        liveRetryCount = 0
                        vm.retry()
                    }
                }
            }
        }
    }
}

private data class ChatLine(val name: String, val text: String, val mine: Boolean)

/** Candidate for the WebCodecs fallback: VOD files only (not HLS/live). */
private fun canFallBackToWebCodecs(url: String, type: ContentType): Boolean {
    val lower = url.lowercase()
    if (type == ContentType.CHANNEL) return false
    if (lower.contains(".m3u8") || lower.contains(".ts") || lower.contains("/live/")) return false
    return true
}

private fun fmtTime(seconds: Double): String {
    if (!seconds.isFinite() || seconds <= 0) return "0:00"
    val total = seconds.toInt()
    val h = total / 3600; val m = (total % 3600) / 60; val s = total % 60
    return if (h > 0) "$h:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}"
    else "$m:${s.toString().padStart(2, '0')}"
}

/** Start the WebCodecs engine on [canvas] for [url]; returns the opaque handle. */
private fun attachWebCodecs(
    canvas: HTMLCanvasElement,
    url: String,
    onReady: (Double) -> Unit,
    onTime: (Double, Double) -> Unit,
    onState: (Boolean) -> Unit,
    onEnded: () -> Unit,
    onError: (String) -> Unit,
): dynamic {
    val cb = js("({})")
    cb.onReady = onReady
    cb.onTime = onTime
    cb.onState = onState
    cb.onEnded = onEnded
    cb.onError = onError
    return window.asDynamic().IptvWC.attach(canvas, url, cb)
}

private fun detachWebCodecs(handle: dynamic) {
    runCatching { window.asDynamic().IptvWC.detach(handle) }
}

private fun attachStream(
    video: HTMLVideoElement,
    rawUrl: String,
    isLive: Boolean,
    srcOverride: String? = null,
    onFatalError: ((String) -> Unit)? = null,
    onTracks: ((List<MediaTrack>, List<MediaTrack>) -> Unit)? = null,
): dynamic {
    // srcOverride = the Watch Together fan-out relay; otherwise the normal proxy.
    // Engine choice still keys off the original URL's extension.
    val src = srcOverride ?: proxied(rawUrl)
    val lower = rawUrl.lowercase()
    return when {
        lower.contains(".m3u8") -> {
            val hlsOk = js("typeof Hls !== 'undefined' && Hls.isSupported()") as Boolean
            if (hlsOk) {
                val hls = js("new Hls()")
                if (onFatalError != null) {
                    // Only fatal errors — hls.js recovers from non-fatal ones itself.
                    hls.on(js("Hls.Events.ERROR"), { _: dynamic, data: dynamic ->
                        if (data != null && (data.fatal as? Boolean) == true) {
                            val kind = (data.type as? String) ?: "unknown"
                            onFatalError("Stream error ($kind) — the stream may be offline or unreachable.")
                        }
                    })
                }
                if (onTracks != null) {
                    // The two lists arrive via separate events — merge the last
                    // known of each before reporting.
                    var lastAudio: List<MediaTrack> = emptyList()
                    var lastText: List<MediaTrack> = emptyList()
                    hls.on(js("Hls.Events.AUDIO_TRACKS_UPDATED"), { _: dynamic, _: dynamic ->
                        lastAudio = hlsTrackList(hls.audioTracks)
                        onTracks(lastAudio, lastText)
                    })
                    hls.on(js("Hls.Events.SUBTITLE_TRACKS_UPDATED"), { _: dynamic, _: dynamic ->
                        lastText = hlsTrackList(hls.subtitleTracks)
                        onTracks(lastAudio, lastText)
                    })
                }
                hls.loadSource(src)
                hls.attachMedia(video)
                hls
            } else {
                video.src = src // Safari plays HLS natively
                null
            }
        }
        // Xtream live is MPEG-TS (.ts) — browsers can't play it natively; use mpegts.js.
        lower.contains(".ts") || lower.contains("/live/") -> {
            val mpegtsOk = js("typeof mpegts !== 'undefined' && mpegts.isSupported()") as Boolean
            if (mpegtsOk) {
                val mp = window.asDynamic().mpegts
                val cfg = js("({})")
                cfg.type = "mpegts"
                cfg.url = src
                cfg.isLive = isLive
                val player = mp.createPlayer(cfg)
                if (onFatalError != null) {
                    player.on(mp.Events.ERROR, { _: dynamic, _: dynamic ->
                        onFatalError("Stream error — the channel may be offline or unreachable.")
                    })
                }
                player.attachMediaElement(video)
                player.load()
                player
            } else {
                video.src = src
                null
            }
        }
        else -> {
            video.src = src // mp4 etc. — native playback
            null
        }
    }
}

private fun detachStream(player: dynamic) {
    runCatching { if (player != null) player.destroy() }
}

/** Map an hls.js track array (audioTracks/subtitleTracks) to MediaTracks: index → id, name/lang → label. */
private fun hlsTrackList(arr: dynamic): List<MediaTrack> {
    val out = mutableListOf<MediaTrack>()
    val n = (arr?.length as? Int) ?: 0
    for (i in 0 until n) {
        val t = arr[i]
        val label = (t.name as? String)?.takeIf { it.isNotBlank() }
            ?: (t.lang as? String)?.takeIf { it.isNotBlank() }
            ?: "Track ${i + 1}"
        out.add(MediaTrack(i.toString(), label))
    }
    return out
}

/**
 * Enumerate native `<video>` tracks. `audioTracks` is Safari-only territory —
 * guarded with typeof checks so unsupported browsers just report nothing.
 * A lone audio track isn't worth a menu, so it's skipped.
 */
private fun reportNativeTracks(v: HTMLVideoElement, report: (List<MediaTrack>, List<MediaTrack>) -> Unit) {
    val vd = v.asDynamic()
    val audio = mutableListOf<MediaTrack>()
    runCatching {
        val at = vd.audioTracks
        if (jsTypeOf(at) != "undefined" && at != null) {
            val n = (at.length as? Int) ?: 0
            if (n > 1) for (i in 0 until n) {
                val t = at[i]
                val label = (t.label as? String)?.takeIf { it.isNotBlank() }
                    ?: (t.language as? String)?.takeIf { it.isNotBlank() }
                    ?: "Track ${i + 1}"
                audio.add(MediaTrack(i.toString(), label))
            }
        }
    }
    val text = mutableListOf<MediaTrack>()
    runCatching {
        val tt = vd.textTracks
        if (jsTypeOf(tt) != "undefined" && tt != null) {
            val n = (tt.length as? Int) ?: 0
            for (i in 0 until n) {
                val t = tt[i]
                val kind = t.kind as? String
                if (kind == "subtitles" || kind == "captions") {
                    val label = (t.label as? String)?.takeIf { it.isNotBlank() }
                        ?: (t.language as? String)?.takeIf { it.isNotBlank() }
                        ?: "Subtitle ${i + 1}"
                    text.add(MediaTrack(i.toString(), label))
                }
            }
        }
    }
    if (audio.isNotEmpty() || text.isNotEmpty()) report(audio, text)
}

@Composable
private fun TrackSectionLabel(text: String) {
    Div(attrs = {
        style {
            property("padding", "6px 10px 4px"); property("font-size", "11px")
            property("font-weight", "700"); property("letter-spacing", "0.06em")
            property("text-transform", "uppercase"); property("color", Theme.textDim)
        }
    }) { Text(text) }
}

@Composable
private fun TrackOption(label: String, selected: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        style {
            property("padding", "7px 10px"); property("border-radius", "7px")
            property("cursor", "pointer"); property("font-size", "13px")
            property("color", if (selected) "#fff" else Theme.text)
            if (selected) property("background", "rgba(124,58,237,0.35)")
        }
        onClick { onClick() }
    }) { Text((if (selected) "✓ " else "") + label) }
}
